import React, { FC, PropsWithChildren, useEffect, useRef, useState } from "react";
import { AckSignInOutButton, NavbarDropdown, useAuth } from "ackbox-weblib-react";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

const IconNavbarDropdown: FC<PropsWithChildren<{ icon: IconProp }>> = (props) => {
  const [isActive, setActive] = useState(false);
  const dropdown = useRef(null as HTMLDivElement | null);
  const toggle = () => {
    setActive(!isActive);
    const links = dropdown.current?.querySelectorAll(".navbar-item") || [];
    links.forEach((link: Element) => link.addEventListener("click", () => setActive(false)));
  };
  const handleClickOutside = (event: MouseEvent) => {
    if (!dropdown?.current?.contains(event.target as Node) && dropdown?.current?.getAttribute("data-active") === "true") {
      setActive(false);
    }
  };
  useEffect(() => {
    window.addEventListener("mousemove", handleClickOutside);
    // returned function will be called on component unmount
    return () => window.removeEventListener("mousemove", handleClickOutside);
  }, []);
  return (
    <div className={"navbar-item has-dropdown" + (isActive ? " is-active" : "")} ref={dropdown} data-active={isActive}>
      <a className="navbar-link" onClick={toggle}> <FontAwesomeIcon icon={props.icon} fixedWidth={true}/></a>
      <div className="navbar-dropdown">
        {props.children}
      </div>
    </div>
  );
};

export const Navigation: FC = () => {
  const { isAuthenticated, username } = useAuth();
  const UserInfo = () => {
    return (
      <div className="navbar-item">
        <span className="navbar-user-logged-in">
          Logged in as <span style={{ fontWeight: "bold" }}>{username}</span>
        </span>
      </div>
    );
  };
  const OverviewMenu = () => {
    return (
      <NavbarDropdown title="Summaries">
        <NavLink className="navbar-item" to="/summaries/overall">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="signal" size="xs" fixedWidth={true}/>
          </div>
          Overall
        </NavLink>
        <NavLink className="navbar-item" to="/summaries/monthly">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="layer-group" size="xs" fixedWidth={true}/>
          </div>
          Monthly
        </NavLink>
        <NavLink className="navbar-item" to="/summaries/yearly">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="layer-group" size="xs" fixedWidth={true}/>
          </div>
          Yearly
        </NavLink>
      </NavbarDropdown>
    );
  };
  const TransactionsMenu = () => {
    return (
      <NavbarDropdown title="Transactions">
        <NavLink className="navbar-item" to="/resources/expenses/create">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="plus" size="xs" fixedWidth={true}/>
          </div>
          New Expense
        </NavLink>
        <NavLink className="navbar-item" to="/resources/expenses">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="list" size="xs" fixedWidth={true}/>
          </div>
          List Expenses
        </NavLink>
        <hr className="navbar-divider"/>
        <NavLink className="navbar-item" to="/resources/incomes/create">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="plus" size="xs" fixedWidth={true}/>
          </div>
          New Income
        </NavLink>
        <NavLink className="navbar-item" to="/resources/incomes">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="list" size="xs" fixedWidth={true}/>
          </div>
          List Incomes
        </NavLink>
        <hr className="navbar-divider"/>
        <NavLink className="navbar-item" to="/resources/transfers/create">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="plus" size="xs" fixedWidth={true}/>
          </div>
          New Transfer
        </NavLink>
        <NavLink className="navbar-item" to="/resources/transfers">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="list" size="xs" fixedWidth={true}/>
          </div>
          List Transfers
        </NavLink>
        <hr className="navbar-divider"/>
        <NavLink className="navbar-item" to="/resources/accounts/create">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="plus" size="xs" fixedWidth={true}/>
          </div>
          New Account
        </NavLink>
        <NavLink className="navbar-item" to="/resources/accounts">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="list" size="xs" fixedWidth={true}/>
          </div>
          List Accounts
        </NavLink>
        <hr className="navbar-divider"/>
        <NavLink className="navbar-item" to="/resources/categories/create">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="plus" size="xs" fixedWidth={true}/>
          </div>
          New Category
        </NavLink>
        <NavLink className="navbar-item" to="/resources/categories">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="list" size="xs" fixedWidth={true}/>
          </div>
          List Categories
        </NavLink>
        <hr className="navbar-divider"/>
        <NavLink className="navbar-item" to="/transactions/import">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="file-import" size="xs" fixedWidth={true}/>
          </div>
          Import Transactions
        </NavLink>
      </NavbarDropdown>
    );
  };
  const PortfolioMenu = () => {
    return (
      <NavbarDropdown title="Portfolio">
        <NavLink className="navbar-item" to="/securities/holdings">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="signal" size="xs" fixedWidth={true}/>
          </div>
          Holdings
        </NavLink>
        <NavLink className="navbar-item" to="/securities/acb/yearly">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="tasks" size="xs" fixedWidth={true}/>
          </div>
          ACB by Year
        </NavLink>
        <NavLink className="navbar-item" to="/securities/acb/securities">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="tasks" size="xs" fixedWidth={true}/>
          </div>
          ACB by Security
        </NavLink>
        <hr className="navbar-divider"/>
        <NavLink className="navbar-item" to="/resources/securities/create">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="plus" size="xs" fixedWidth={true}/>
          </div>
          New Security
        </NavLink>
        <NavLink className="navbar-item" to="/resources/securities">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="list" size="xs" fixedWidth={true}/>
          </div>
          List Securities
        </NavLink>
        <hr className="navbar-divider"/>
        <NavLink className="navbar-item" to="/resources/trades/create">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="plus" size="xs" fixedWidth={true}/>
          </div>
          New Trade
        </NavLink>
        <NavLink className="navbar-item" to="/resources/trades">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="list" size="xs" fixedWidth={true}/>
          </div>
          List Trades
        </NavLink>
        <hr className="navbar-divider"/>
        <NavLink className="navbar-item" to="/resources/security-splits/create">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="plus" size="xs" fixedWidth={true}/>
          </div>
          New Security Split
        </NavLink>
        <NavLink className="navbar-item" to="/resources/security-splits">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="list" size="xs" fixedWidth={true}/>
          </div>
          List Security Split
        </NavLink>
      </NavbarDropdown>
    );
  };
  const SimulationsMenu = () => {
    return (
      <NavbarDropdown title="Simulations">
        <NavLink className="navbar-item" to="/simulations">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="flask" size="xs" fixedWidth={true}/>
          </div>
          Dashboard
        </NavLink>
        <hr className="navbar-divider"/>
        <NavLink className="navbar-item" to="/resources/simulations/create">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="plus" size="xs" fixedWidth={true}/>
          </div>
          New Simulation
        </NavLink>
        <NavLink className="navbar-item" to="/resources/simulations">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="list" size="xs" fixedWidth={true}/>
          </div>
          List Simulations
        </NavLink>
      </NavbarDropdown>
    );
  };
  const SettingsMenu = () => {
    return (
      <IconNavbarDropdown icon="cog">
        <NavLink className="navbar-item" to="/settings">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="cogs" fixedWidth={true}/>
          </div>
          Settings
        </NavLink>
        <NavLink className="navbar-item" to="/changelogs">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="list" fixedWidth={true}/>
          </div>
          Changelogs
        </NavLink>
        <NavLink className="navbar-item" to="/statusz">
          <div className="navbar-icon">
            <FontAwesomeIcon icon="info-circle" fixedWidth={true}/>
          </div>
          System Info
        </NavLink>
      </IconNavbarDropdown>
    );
  };
  return (
    <>
      <div className="navbar-start">
        {isAuthenticated && <OverviewMenu/>}
        {isAuthenticated && <TransactionsMenu/>}
        {isAuthenticated && <PortfolioMenu/>}
        {isAuthenticated && <SimulationsMenu/>}
      </div>
      <div className="navbar-end">
        {isAuthenticated && <SettingsMenu/>}
        {isAuthenticated && <UserInfo/>}
        <div className="navbar-item">
          <AckSignInOutButton/>
        </div>
      </div>
    </>
  );
};
